'use client'

import { useState } from 'react'
import { Minus, Plus } from 'lucide-react'
import { Emoji } from '@/components/between-sessions/Emoji'

// Combined cohort dates + FAQ. Two objections handled in one section:
// "when does the next one start?" (top strip) and "what if I want to
// cancel / rematch / upgrade?" (accordion below).

// Application deadlines are ~1 week before each cohort starts — that
// gives time for supervisor + dyad partner matching before the cohort
// kicks off. Placeholder dates below; Cherry to confirm the actual
// cutoff for each cohort.
const COHORTS = [
  {
    name: 'Fall 26',
    date: 'September 1, 2026',
    applyBy: 'Apply by August 25',
    highlight: true,
  },
  {
    name: 'Winter 27',
    date: 'January 5, 2027',
    applyBy: 'Apply by December 29',
    highlight: false,
  },
  {
    name: 'Spring 27',
    date: 'May 4, 2027',
    applyBy: 'Apply by April 27',
    highlight: false,
  },
]

const FAQS: { q: string; a: string }[] = [
  {
    q: 'Do I have to commit to 4 months?',
    a: "Only Community, Complete, and Complete+ carry a 4-month commitment — they run as cohorts (Winter, Spring, Fall) with structured supervision that only works if the group stays together. Core is month-to-month: start anytime, cancel anytime, no cohort.",
  },
  {
    q: 'How does cancellation work?',
    a: "Core: cancel anytime from your portal — access continues to the end of your current billing month. Community, Complete, and Complete+: you can't cancel mid-cohort (dyads and groups depend on continuity), but you can choose not to renew at the end of the 4-month cycle. Renewal reminders go out 2 weeks before the cohort ends.",
  },
  {
    q: 'What if I don’t click with my supervisor?',
    a: "Before your cohort starts, you pick from every available supervisor — so most members find their fit upfront. If it's not clicking mid-cohort, message support and we'll do our best to help (a mid-cohort switch depends on team availability). At the end of every 4-month cohort you can freely switch to any supervisor whose calendar has openings.",
  },
  {
    q: 'Can I upgrade or downgrade tiers?',
    a: "You can switch tiers at the boundary between cohorts (Sept 1, Jan 1, May 1). Core members can upgrade to a cohort tier anytime — the cohort tier just starts on the next cohort date. Mid-cohort tier changes aren't supported because the supervision structure changes between tiers.",
  },
  {
    q: 'How do referrals actually work?',
    a: "When you're ready to take clients, you turn on matching in your portal and set your caseload availability. Alli's client-matching engine pairs incoming Canadians looking for therapy against your modality, price point, and availability. You accept, decline, or refer to another BES therapist — all inside the platform. No extra fees; your full rate goes to you.",
  },
  {
    q: 'What if I’m not a therapist yet — can I still join?',
    a: "Yes. Between Sessions welcomes therapists at any stage — practicum students, associate therapists, RPs building toward independent practice, and fully-licensed clinicians who want a community. Core is often the right entry point for students since it's flexible and doesn't require a 4-month cohort commitment.",
  },
]

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <li className="border-b border-[#ececec] last:border-b-0">
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 py-5 text-left group"
      >
        <span className="font-display font-semibold text-[15px] sm:text-[16px] text-gray-900 group-hover:text-black transition-colors">
          {q}
        </span>
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-white border border-[#ececec] flex items-center justify-center text-gray-500 group-hover:text-gray-900 group-hover:border-bs-secondary-200 transition-all">
          {open ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
        </span>
      </button>
      {open && (
        <div className="pb-5 pr-10 animate-fade-up">
          <p className="text-[14px] text-gray-600 leading-relaxed">{a}</p>
        </div>
      )}
    </li>
  )
}

export function LandingCohortsAndFaq() {
  return (
    <section className="px-6 py-24 sm:py-28 bg-[#faf9f6]">
      <div className="max-w-4xl mx-auto">
        {/* Cohort strip — quick urgency signal */}
        <div className="text-center mb-12">
          <p className="text-[11px] uppercase tracking-[0.22em] text-bs-secondary-700 font-display font-semibold mb-4">
            Upcoming cohorts
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {COHORTS.map(c => (
              <div
                key={c.name}
                className={
                  'rounded-2xl px-5 py-5 flex flex-col items-center gap-1 transition-all duration-300 ' +
                  (c.highlight
                    ? 'bg-white border-2 border-bs-secondary-500 shadow-[0_16px_36px_-16px_rgba(108,95,199,0.35)]'
                    : 'bg-white border border-[#ececec]')
                }
              >
                <p
                  className={
                    'text-[10px] uppercase tracking-[0.22em] font-display font-semibold ' +
                    (c.highlight ? 'text-bs-secondary-700' : 'text-gray-500')
                  }
                >
                  {c.highlight ? 'Next intake' : c.name}
                </p>
                <p className="font-display font-bold text-lg text-gray-900 leading-tight">
                  {c.date}
                </p>
                <div className="mt-2 pt-2 border-t border-[#ececec] w-full text-center">
                  <p
                    className={
                      'text-[11px] font-display font-semibold ' +
                      (c.highlight ? 'text-bs-secondary-700' : 'text-gray-600')
                    }
                  >
                    {c.applyBy}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white border border-[#ececec] shadow-[0_6px_16px_-8px_rgba(20,20,20,0.15)] mb-5 text-bs-secondary-500">
            <Emoji name="fluent-emoji-high-contrast:red-question-mark" size={28} />
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl leading-[1.05] tracking-tight text-gray-900 mb-3">
            Questions,{' '}
            <span className="italic text-bs-secondary-500">answered</span>.
          </h2>
          <p className="text-[14px] text-gray-600">
            The things people ask us most before joining.
          </p>
        </div>

        <ul className="rounded-[1.4rem] border border-[#ececec] bg-white px-6 shadow-[0_18px_40px_-22px_rgba(0,0,0,0.18)]">
          {FAQS.map((faq, i) => (
            <FaqItem key={i} q={faq.q} a={faq.a} />
          ))}
        </ul>

        <p className="mt-6 text-center text-[13px] text-gray-500">
          Still curious? Email{' '}
          <a
            href="mailto:hello@alli.io"
            className="text-bs-secondary-600 font-display font-semibold hover:text-bs-secondary-700 underline underline-offset-2"
          >
            hello@alli.io
          </a>
          .
        </p>
      </div>
    </section>
  )
}
